import { z } from 'zod';
import { AloftObject } from './AloftObject.js';
import { Lens } from './Lens.js';
import { assert } from './utils/assert.js';
const schCurrencyCode = z.literal('usd');
const currencyToDollarLikeMultiplier = { usd: 100 };
const currencySymbols = { usd: '$' };
export const schAloftPriceJSON = z.object({
    $type: z.literal('AloftPrice'),
    unitAmount: z.number(),
    currency: schCurrencyCode,
});
export class AloftPrice extends AloftObject {
    unitAmount;
    currency;
    constructor(unitAmount, currency) {
        super();
        this.unitAmount = unitAmount;
        this.currency = currency;
    }
    static isAloftPrice(x) {
        return x instanceof AloftPrice;
    }
    static min(prices) {
        const definedPrices = prices.filter((price) => price !== undefined && price !== null);
        if (definedPrices.length === 0)
            return undefined;
        return definedPrices.reduce((min, price) => (price.lt(min) ? price : min), definedPrices[0]);
    }
    static max(prices) {
        const definedPrices = prices.filter((price) => price !== undefined && price !== null);
        if (definedPrices.length === 0)
            return undefined;
        return definedPrices.reduce((max, price) => (price.gt(max) ? price : max), definedPrices[0]);
    }
    compare(that) {
        assert(this.currency === that.currency, 'Cannot mix currencies for comparison');
        return this.unitAmount - that.unitAmount;
    }
    // NOTE: toJSON() and schema.parse() should be inverses of each other!
    toJSON() {
        return {
            $type: 'AloftPrice',
            unitAmount: this.unitAmount,
            currency: this.currency,
        };
    }
    toRedux() {
        return this.toJSON();
    }
    static schema = z
        .union([schAloftPriceJSON, z.custom((x) => x instanceof AloftPrice)])
        .transform(({ unitAmount, currency }) => new AloftPrice(unitAmount, currency));
    static zero = new AloftPrice(0, 'usd');
    static fromUnitAmount(unitAmount, currencyCode = 'usd') {
        if (unitAmount === null || unitAmount === undefined)
            return unitAmount;
        // Not sure if floor(), round(), or trunc() is more sensible here
        const amount = Math.round(typeof unitAmount === 'string' ? Number.parseInt(unitAmount, 10) : unitAmount);
        if (!Number.isFinite(amount)) {
            throw new Error(`cannot construct AloftPrice from ${unitAmount}`);
        }
        return new AloftPrice(amount === -0 ? 0 : amount, currencyCode);
    }
    static fromDollarLike(dollarLike, currencyCode = 'usd') {
        if (dollarLike === undefined || dollarLike === null)
            return dollarLike;
        if (typeof dollarLike === 'string' && dollarLike.trim() === '')
            return AloftPrice.zero;
        const unitAmount = (typeof dollarLike === 'string'
            ? Number.parseInt(dollarLike
                .replaceAll(currencySymbols[currencyCode], '')
                .replaceAll(',', '')
                .replace('\u2212', '\u002d'), 10)
            : dollarLike) * currencyToDollarLikeMultiplier[currencyCode];
        if (Number.isNaN(unitAmount)) {
            throw Error(`Cannot convert ${dollarLike} to price`);
        }
        return AloftPrice.fromUnitAmount(unitAmount, currencyCode);
    }
    toString(
    // FIXME: should use a param object instead of listing booleans
    dollarsOnly = false, noCurrencySymbol = false) {
        const currency = currencySymbols[this.currency];
        const dollarLike = Math.trunc(this.unitAmount / 100);
        const negativeSymbol = dollarLike < 0 ? '-' : '';
        const decimal = dollarsOnly ? '' : '.';
        const centLike = dollarsOnly ? '' : `${Math.abs(this.unitAmount % 100)}`.padStart(2, '0');
        return `${negativeSymbol}${noCurrencySymbol ? '' : currency}${Math.abs(dollarLike).toLocaleString('en-US')}${decimal}${centLike}`;
    }
    toAbbrString(fractionDigits) {
        const { unitAmount, currency } = this;
        if (currency !== 'usd')
            return this.toString();
        const dollars = Math.floor(unitAmount / 100);
        if (dollars > 1e9)
            return `$${(dollars / 1e9).toFixed(fractionDigits ?? 1)}B`;
        if (dollars > 1e6)
            return `$${(dollars / 1e6).toFixed(fractionDigits ?? 1)}M`;
        if (dollars > 1e3)
            return `$${(dollars / 1e3).toFixed(fractionDigits ?? 1)}k`;
        if (unitAmount % 100 === 0)
            return `$${dollars}`;
        return `$${(unitAmount / 100).toFixed(2)}`;
    }
    toDollarLike() {
        return this.unitAmount / currencyToDollarLikeMultiplier[this.currency];
    }
    /**
     * Round price to nearest whole dollar
     * E.g. $100.33 becomes $100
     */
    roundToDollarLike() {
        return AloftPrice.fromDollarLike(Math.round(this.toDollarLike()), this.currency);
    }
    sub(that) {
        if (that === undefined || that === null)
            return this;
        assert(this.currency === that.currency, 'Cannot mix currencies for subtraction');
        return new AloftPrice(this.unitAmount - that.unitAmount, this.currency);
    }
    add(that) {
        if (that === undefined || that === null)
            return this;
        assert(this.currency === that.currency, 'Cannot mix currencies for addition');
        return new AloftPrice(this.unitAmount + that.unitAmount, this.currency);
    }
    static add(...prices) {
        return prices.reduce((a, b) => a.add(b), AloftPrice.zero);
    }
    static addWithNulls(...prices) {
        return prices.reduce((a, b) => a?.add(b) ?? b ?? null, null);
    }
    abs() {
        return AloftPrice.fromUnitAmount(Math.abs(this.unitAmount), this.currency);
    }
    mul(by) {
        if (by === undefined || by === null)
            return by;
        return AloftPrice.fromUnitAmount(this.unitAmount * by, this.currency);
    }
    div(by) {
        if (by === undefined || by === null)
            return by;
        return AloftPrice.fromUnitAmount(this.unitAmount / by, this.currency);
    }
    ratio(divisor) {
        if (divisor === undefined || divisor === null)
            return divisor;
        assert(this.currency === divisor.currency, 'Cannot mix currencies for ratios');
        return this.unitAmount / divisor.unitAmount;
    }
    roundTo(round) {
        if (round === undefined || round === null)
            return null;
        assert(this.currency === round.currency, 'Cannot mix currencies for rounding');
        return round.mul(Math.round(this.ratio(round)));
    }
    // NumericPrice
    // -----------------------------------------------------------------------------
    /**
     * NOTE: every use of NumericPrice is a code debt liability for if we ever
     * use non-dollar prices. Please try to use NumericPrice instead of number
     * if you're working this way.
     */
    /** @deprecated */
    toNumericPrice() {
        return this.unitAmount / 100;
    }
    static fromNumericPrice(price, currency = 'usd') {
        if (price === null)
            return null;
        if (price === undefined)
            return undefined;
        return AloftPrice.fromUnitAmount(price * 100, currency);
    }
    static dollarLikeLens = new Lens((price) => price.toDollarLike(), (price, dollars) => AloftPrice.fromDollarLike(dollars, price.currency));
    static dollarLensOpt = AloftPrice.dollarLikeLens.optional(AloftPrice.fromDollarLike(0, 'usd'));
    static dollarLensNull = AloftPrice.dollarLikeLens.nullable(AloftPrice.fromDollarLike(0, 'usd'));
}
//# sourceMappingURL=AloftPrice.js.map